import { FC, useMemo, useState, useRef } from "react";
import { IDropdownMenuItem } from "@atomaro/ui-kit/components/DropdownMenu/types";
import DropdownMenu from "@atomaro/ui-kit/components/DropdownMenu/DropdownMenu";
import Button from "@atomaro/ui-kit/components/Button/Button/Button";
import Input from "@atomaro/ui-kit/components/Input/Input";
import { HivesSelectWrapper, HivesSelectHeader, HivesSelectRange, HivesSelectButton } from "../styles";
import { toRem } from "shared/helpers";
import { COLORS } from "shared/consts";

type HivesSelectWithActionsProps = {
  value: IDropdownMenuItem[];
  items: IDropdownMenuItem[];
  onChange: (value: IDropdownMenuItem[]) => void;
  placeholder?: string;
  error?: string;
  style?: React.CSSProperties;
};

export const HivesSelectWithActions: FC<HivesSelectWithActionsProps> = ({
  value = [],
  items,
  onChange,
  placeholder = "Номер улья",
  error,
  style,
}) => {
  const [hiveNumberFrom, setHiveNumberFrom] = useState("");
  const [hiveNumberTo, setHiveNumberTo] = useState("");
  const [isOpened, setIsOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedKeys = useMemo(() => {
    return value.map((item) => item.key);
  }, [value]);

  const handleSelectAll = () => {
    onChange(items);
  };

  const handleClearAll = () => {
    onChange([]);
    setHiveNumberFrom("");
    setHiveNumberTo("");
  };

  const handleRangeApply = () => {
    const from = parseInt(hiveNumberFrom);
    const to = parseInt(hiveNumberTo);

    if (!from && !to) {
      return;
    }

    const fromNum = from || 0;
    const toNum = to || Infinity;

    // Фильтруем items по номерам ульев, извлекая номер из value (формат "№{number}")
    const filteredItems = items.filter((item) => {
      const match = String(item.value).match(/№(\d+)/);
      if (!match) return false;
      const number = parseInt(match[1]);
      return number >= fromNum && number <= toNum;
    });

    if (filteredItems.length > 0) {
      onChange(filteredItems);
    } else {
      onChange([]);
    }
  };

  const handleChange = (keys: (string | number)[]) => {
    const keysSet = new Set(keys);
    const selectedItems = items.filter((item) => keysSet.has(item.key));
    onChange(selectedItems);
  };

  const displayValue = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0].value;
    return `${value.length} выбрано`;
  }, [value, placeholder]);

  return (
    <HivesSelectWrapper style={style}>
      <DropdownMenu
        isOpened={isOpened}
        onClose={() => setIsOpened(false)}
        isMultiselect
        hideOnSelect={false}
        value={selectedKeys as (string | number)[]}
        placement="bottom"
        style={{
          background: COLORS.BG.SURFACE2,
          width: style?.width || toRem(220),
          maxHeight: toRem(500),
          overflowY: "auto",
        }}
        items={items}
        onChange={handleChange}
        headerSlot={() => (
          <HivesSelectHeader
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div 
              style={{ display: "flex", gap: toRem(8), marginBottom: toRem(12) }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Button
                label="Выбрать все"
                variant="ghost"
                colorScheme="neutral"
                size="s"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSelectAll();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ flex: 1 }}
              />
              <Button
                label="Очистить все"
                variant="ghost"
                colorScheme="neutral"
                size="s"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleClearAll();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ flex: 1 }}
              />
            </div>
            <HivesSelectRange
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div 
                style={{ display: "flex", gap: toRem(8) }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Input
                  label="От"
                  value={hiveNumberFrom}
                  onChange={(e) => {
                    e.stopPropagation();
                    setHiveNumberFrom(e.target.value.replace(/[^\d]/g, ""));
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => {
                    e.stopPropagation();
                    e.target.select();
                  }}
                  inputMode="numeric"
                  placeholder=""
                  style={{ flex: 1 }}
                />
                <Input
                  label="До"
                  value={hiveNumberTo}
                  onChange={(e) => {
                    e.stopPropagation();
                    setHiveNumberTo(e.target.value.replace(/[^\d]/g, ""));
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => {
                    e.stopPropagation();
                    e.target.select();
                  }}
                  inputMode="numeric"
                  placeholder=""
                  style={{ flex: 1 }}
                />
              </div>
              <Button
                label="Применить"
                variant="primary"
                colorScheme="accent"
                size="s"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRangeApply();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={!hiveNumberFrom || !hiveNumberTo}
                style={{ marginTop: toRem(8), width: "100%" }}
              />
            </HivesSelectRange>
          </HivesSelectHeader>
        )}
      >
        <HivesSelectButton
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpened(!isOpened)}
          $hasError={!!error}
        >
          {displayValue}
        </HivesSelectButton>
      </DropdownMenu>
      {error && <div style={{ color: COLORS.ACCENT.DEFAULT, fontSize: toRem(12), marginTop: toRem(4) }}>{error}</div>}
    </HivesSelectWrapper>
  );
};

